import React, { useEffect, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import { Request, RequestStatus } from "@/types/request";
import { Room } from "@/types/room";
import { KanbanCard, KanbanCardOverlay } from "./KanbanCard";
import { getStatusLabel } from "@/constants/requestConstants";
import { cn } from "@/lib/utils";
import { getRequestWorkflowStatus } from "@/components/pages/requests/utils/taskAssignment";
import { useUpdateTask } from "@/components/pages/requests/hooks/tasks.hooks";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const COLUMN_BADGE_CLASSES: Record<RequestStatus, string> = {
  [RequestStatus.NEW_TASK]: "bg-orange-100 text-orange-600",
  [RequestStatus.APPROVED]: "bg-indigo-100 text-indigo-600",
  [RequestStatus.TO_DO]: "bg-purple-100 text-purple-600",
  [RequestStatus.IN_PROGRESS]: "bg-blue-100 text-blue-600",
  [RequestStatus.ON_HOLD]: "bg-yellow-100 text-yellow-700",
  [RequestStatus.COMPLETED]: "bg-green-100 text-green-600",
  [RequestStatus.DONE]: "bg-slate-100 text-slate-600",
  [RequestStatus.CANCELED]: "bg-red-100 text-red-600",
};

const KANBAN_COLUMNS: RequestStatus[] = [
  RequestStatus.NEW_TASK,
  RequestStatus.APPROVED,
  RequestStatus.TO_DO,
  RequestStatus.IN_PROGRESS,
  RequestStatus.ON_HOLD,
  RequestStatus.COMPLETED,
  RequestStatus.DONE,
  RequestStatus.CANCELED,
];

type KanbanRoomCategoryInfo = { name: string; pmsType?: string };

const resolveKanbanRoomCategory = (
  room: Room | null | undefined,
  byId?: Record<string, KanbanRoomCategoryInfo>,
): KanbanRoomCategoryInfo | undefined => {
  if (!room) {
    return undefined;
  }
  if (room.category) {
    return {
      name: room.category.name,
      pmsType: room.category.pmsType,
    };
  }
  const id = room.categoryId;
  if (id && byId?.[id]) {
    return byId[id];
  }
  return undefined;
};

const applyTaskStatusToRequests = (
  requestList: Request[],
  requestId: string,
  taskId: string,
  newStatus: RequestStatus,
): Request[] =>
  requestList.map((r) => {
    if (r.id !== requestId) return r;
    const tasks = r.tasks?.map((item) =>
      item.id === taskId ? { ...item, status: newStatus } : item,
    );
    return { ...r, tasks };
  });

interface KanbanColumnProps {
  status: RequestStatus;
  requests: Request[];
  onRequestClick: (id: string) => void;
  onUpdateRequest: (id: string, data: Partial<Request>) => void;
  roomCategoryById?: Record<string, KanbanRoomCategoryInfo>;
  onEditRequest?: (
    request: Request,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  status,
  requests,
  onRequestClick,
  onUpdateRequest,
  roomCategoryById,
  onEditRequest,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: status });

  return (
    <div className="flex flex-col w-fit flex-1">
      <div className="flex items-center gap-2 mb-3 px-1">
        <h3 className="font-semibold text-sm whitespace-nowrap">
          {getStatusLabel(status)}
        </h3>
        <span
          className={cn(
            "text-xs px-2 py-0.5 rounded-full font-medium shrink-0",
            COLUMN_BADGE_CLASSES[status],
          )}
        >
          {requests.length}
        </span>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          "flex flex-col gap-2 min-h-[120px] min-w-[300px] rounded-lg p-2 transition-colors flex-1 overflow-hidden",
          isOver && "bg-muted/60 ring-2 ring-primary/20",
        )}
      >
        {requests.map((request) => (
          <KanbanCard
            key={request.id}
            request={request}
            roomCategory={resolveKanbanRoomCategory(
              request.room,
              roomCategoryById,
            )}
            onClick={() => onRequestClick(request.id)}
            onUpdateRequest={onUpdateRequest}
            onEditRequest={onEditRequest}
          />
        ))}
      </div>
    </div>
  );
};

export const RequestsKanban: React.FC<{
  requests: Request[];
  onRequestClick: (id: string) => void;
  onUpdateRequest: (id: string, data: Partial<Request>) => void;
  roomCategoryById?: Record<string, KanbanRoomCategoryInfo>;
  onEditRequest?: (
    request: Request,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;
}> = ({
  requests,
  onRequestClick,
  onUpdateRequest,
  roomCategoryById,
  onEditRequest,
}) => {
  const { t } = useTranslation();
  const updateTaskMutation = useUpdateTask();
  const [localRequests, setLocalRequests] = useState<Request[]>(requests);
  const [activeRequest, setActiveRequest] = useState<Request | null>(null);

  useEffect(() => {
    setLocalRequests(requests);
  }, [requests]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const found = localRequests.find((r) => r.id === String(event.active.id));
    setActiveRequest(found ?? null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveRequest(null);

    if (!over) return;

    const requestId = String(active.id);
    const newStatus = over.id as RequestStatus;

    const request = localRequests.find((r) => r.id === requestId);
    if (!request) return;

    const currentStatus = getRequestWorkflowStatus(request);
    if (currentStatus === newStatus) return;

    const task = request.tasks?.[0];
    if (!task?.id) {
      toast.error(t("requests.form.failedToUpdateRequest"));
      return;
    }

    const previousRequests = localRequests;
    setLocalRequests((prev) =>
      applyTaskStatusToRequests(prev, requestId, task.id, newStatus),
    );

    try {
      await updateTaskMutation.mutateAsync({
        id: task.id,
        data: { status: newStatus },
      });
      toast.success(t("requests.messages.requestUpdatedSuccessfully"));
    } catch {
      setLocalRequests(previousRequests);
      toast.error(t("requests.form.failedToUpdateRequest"));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={(e) => {
        void handleDragEnd(e);
      }}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {KANBAN_COLUMNS.map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            requests={localRequests.filter(
              (r) => getRequestWorkflowStatus(r) === status,
            )}
            onRequestClick={onRequestClick}
            onUpdateRequest={onUpdateRequest}
            roomCategoryById={roomCategoryById}
            onEditRequest={onEditRequest}
          />
        ))}
      </div>
      <DragOverlay>
        {activeRequest ? (
          <KanbanCardOverlay
            request={activeRequest}
            roomCategory={resolveKanbanRoomCategory(
              activeRequest.room,
              roomCategoryById,
            )}
            onUpdateRequest={onUpdateRequest}
            onEditRequest={onEditRequest}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
